import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { FeaturesTableV2 } from '@/features/features/components/FeaturesTableV2';
import { queryFeatures } from '@/features/features/data';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { gridPaths, queryKeys } from '@/shared/hooks';
import {
  featuresTableDefinition,
  featuresSortFieldMap,
  type FeatureDeliveryMode,
  type FeaturesSortDirection,
  type FeaturesSortKey,
} from './featuresTableDefinition';
import type { TagOption } from '@/shared/data/tags';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { FeatureDto, ProjectDto, QueryResult } from '@flowqa/shared';

type FeaturesTablePanelProps = {
  featureMode: FeatureDeliveryMode;
  fixedFilters: GridQueryInput['filters'];
  noRowsText: string;
  projectOptions?: Array<
    Pick<ProjectDto, 'badgeChars' | 'badgeColor' | 'badgeEnabled' | 'id' | 'name'>
  >;
  searchValue?: string;
  tagOptions?: TagOption[];
};

export const FeaturesTablePanel = ({
  featureMode,
  fixedFilters,
  noRowsText,
  projectOptions = [],
  searchValue = '',
  tagOptions = [],
}: FeaturesTablePanelProps): ReactElement => {
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const [sortKey, setSortKey] = useState<FeaturesSortKey>(featuresTableDefinition.defaultSortKey);
  const [sortDirection, setSortDirection] = useState<FeaturesSortDirection>(
    featuresTableDefinition.defaultSortDirection,
  );
  useEffect(() => {
    if (featureMode === 'released' && sortKey === 'plannedFor') {
      setSortKey('releasedAt');
      return;
    }
    if (featureMode === 'inProgress' && sortKey === 'releasedAt') {
      setSortKey('plannedFor');
    }
  }, [featureMode, sortKey]);
  const sort = useMemo(
    () => ({ field: sortKey, direction: sortDirection }),
    [sortDirection, sortKey],
  );
  const fixedFiltersKey = useMemo(() => JSON.stringify(fixedFilters), [fixedFilters]);
  const queryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<FeatureDto>> =>
      queryFeatures(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        filters: input.filters,
        sorts: input.sorts.map((inputSort) => ({
          field: featuresSortFieldMap[inputSort.field as FeaturesSortKey],
          direction: inputSort.direction,
        })),
      }),
    [selectedOrganisationId, selectedProjectId],
  );

  return (
    <FeaturesTableV2
      fixedFilters={fixedFilters}
      queryFn={queryFn}
      queryKey={[
        ...queryKeys.dataGrid(gridPaths.features),
        selectedOrganisationId ?? null,
        selectedProjectId ?? null,
        featureMode,
        sortKey,
        sortDirection,
        fixedFiltersKey,
      ]}
      noRowsText={noRowsText}
      projectOptions={projectOptions}
      search={searchValue}
      selectedProjectId={selectedProjectId}
      featureMode={featureMode}
      sort={sort}
      tagOptionsOverride={tagOptions}
      onSortChange={(nextSort) => {
        setSortKey(nextSort.field as FeaturesSortKey);
        setSortDirection(nextSort.direction as FeaturesSortDirection);
      }}
    />
  );
};
